import { useEffect, useState } from 'react';
import { AlertTriangle, FileText, Loader2, RefreshCw, SearchCheck, Send, Settings, ShoppingCart } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/cn';
import { useNotifications } from '../../store/useStore';
import { eventiService, type EsitoLetturaOrdini } from '../../lib/eventi/eventiService';
import { messaggioErrore } from '../../lib/eventi/errori';
import { segnaBigliettiVisti } from '../../lib/eventi/avvisiBiglietti';
import {
  contaBigliettiEvento,
  letturaBloccata,
  linkRegistrazione,
  ordineDaCompletare,
  perOrdine,
  type OrdineConBiglietti,
} from '../../lib/eventi/biglietti';
import type { PostaAcquirente } from '../../lib/eventi/daSentire';
import { dataOraIt } from '../../lib/eventi/stati';
import type { BigliettoEvento, Evento, StatoLetturaOrdini, StatoPdf } from '../../lib/eventi/tipi';
import { CartaOrdine } from './CartaOrdine';
import { CopiaLink } from './CopiaLink';
import { DaSentire, RigaEmailOrdine } from './DaSentire';
import { Pannello } from './Pannello';
import type { SchedaEvento } from './schede';
import { usePostaAcquirenti } from './usePostaAcquirenti';

type Props = {
  evento: Evento;
  biglietti: BigliettoEvento[];
  statoLettura: StatoLetturaOrdini | null;
  /** I biglietti non si sono potuti leggere (es. aggiornamento del database non ancora fatto). */
  errore: string | null;
  onRicarica: () => Promise<void>;
  onApri: (scheda: SchedaEvento) => void;
};


function riepilogoProva(e: EsitoLetturaOrdini): string {
  const righe = [`${e.ordini_letti} ordini sul sito, ${e.ordini_pagati} pagati con biglietti.`];
  if (e.senza_email > 0) righe.push(`${e.senza_email} senza un'email valida: non riceveranno il link.`);
  return righe.join(' ');
}

function riepilogoLettura(e: EsitoLetturaOrdini): string {
  const righe = [`${e.ordini_letti} ordini letti, ${e.biglietti_nuovi} biglietti nuovi.`];
  if (e.annullati > 0) righe.push(`${e.annullati} biglietti annullati.`);
  if (e.acquirenti_rimandati > 0) righe.push(`${e.acquirenti_rimandati} acquirenti si collegano al prossimo giro.`);
  if (e.errori.length > 0) righe.push(`${e.errori.length} ordini non salvati: ${e.errori[0].messaggio}`);
  return righe.join(' ');
}

const STATO_BIGLIETTO = {
  registrato: { testo: 'Registrato', classe: 'border-green-200 bg-green-50 text-green-700' },
  da_registrare: { testo: 'Da registrare', classe: 'border-amber-200 bg-amber-50 text-amber-800' },
  annullato: { testo: 'Annullato', classe: 'border-red-200 bg-red-50 text-red-700' },
} as const;

/** Cosa dire del PDF di un biglietto registrato, e quando si puo' rimandare. */
function descriviPdf(pdf: StatoPdf, invioAcceso: boolean): { testo: string; dettaglio?: string; classe: string; rimandabile: boolean } {
  switch (pdf.stato) {
    case 'in_attesa':
      return invioAcceso
        ? { testo: 'PDF: parte a breve', classe: 'text-neutral-500', rimandabile: false }
        : { testo: 'PDF: invio spento', dettaglio: 'Si accende in Impostazioni, Biglietti dal sito.', classe: 'text-neutral-500', rimandabile: false };
    case 'in_coda':
      return { testo: 'PDF in coda', dettaglio: pdf.errore ? `Nuovo tentativo dopo: ${pdf.errore}` : undefined, classe: 'text-amber-700', rimandabile: false };
    case 'inviato':
      return { testo: `PDF inviato il ${dataOraIt(pdf.quando)}`, classe: 'text-green-700', rimandabile: true };
    case 'errore':
      return { testo: 'PDF non consegnato', dettaglio: pdf.errore ?? undefined, classe: 'text-red-700', rimandabile: true };
    case 'annullato':
      return { testo: 'PDF non mandato', classe: 'text-neutral-500', rimandabile: true };
    case 'simulato':
      return { testo: 'PDF simulato (email in modalità prova)', classe: 'text-neutral-500', rimandabile: true };
  }
}

/** Rimette in coda l'email col biglietto per il partecipante: "non mi e' arrivato". */
function Rimanda({ id, onRicarica }: { id: string; onRicarica: () => Promise<void> }) {
  const { addNotification } = useNotifications();
  const [inCorso, setInCorso] = useState(false);
  const rimanda = async () => {
    setInCorso(true);
    try {
      await eventiService.rimandaBiglietto(id);
      addNotification({ type: 'success', title: 'Biglietto in coda', message: 'Parte entro pochi minuti.' });
      await onRicarica();
    } catch (e) {
      addNotification({ type: 'error', title: 'Non riesco a rimandare il biglietto', message: messaggioErrore(e) });
    } finally {
      setInCorso(false);
    }
  };
  return (
    <button
      type="button"
      onClick={() => void rimanda()}
      disabled={inCorso}
      title="Rimanda al partecipante l'email con il biglietto in PDF"
      className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 shadow-sm transition-colors hover:border-gray-300 hover:bg-gray-50 disabled:opacity-50"
    >
      {inCorso ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
      Rimanda
    </button>
  );
}

function RigaBiglietto({ b, evento, onRicarica }: { b: BigliettoEvento; evento: Evento; onRicarica: () => Promise<void> }) {
  const stato = STATO_BIGLIETTO[b.stato];
  const pdf = b.pdf ? descriviPdf(b.pdf, evento.biglietti_pdf) : null;
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1.5 py-2 text-sm">
      <span className="w-36 flex-shrink-0 leading-tight">
        <span className="block text-xs font-medium text-neutral-500">Biglietto {b.numero}</span>
        <span className="block truncate text-[11px] text-neutral-400" title={b.tipo}>{b.tipo}</span>
      </span>
      <span className={cn('rounded-full border px-2 py-0.5 text-[11px] font-semibold', stato.classe)}>{stato.testo}</span>
      {b.stato === 'registrato' && b.partecipante && (
        <span className="min-w-0 flex-1 truncate">
          <span className="font-medium text-gray-900">{b.partecipante.nome}</span>
          <span className="ml-2 text-xs text-neutral-500">{b.partecipante.email}</span>
        </span>
      )}
      {b.stato === 'da_registrare' && (
        <span className="flex flex-1 justify-end sm:justify-start">
          <CopiaLink
            link={linkRegistrazione(evento.url_registrazione, b.token, window.location.origin)}
            titolo="Copia il link per registrare questo biglietto"
          />
        </span>
      )}
      {pdf && (
        <span className="flex w-full items-center gap-2 sm:w-auto">
          <span className={cn('inline-flex items-center gap-1 text-[11px] font-medium', pdf.classe)} title={pdf.dettaglio}>
            <FileText className="h-3.5 w-3.5" />
            {pdf.testo}
          </span>
          {pdf.rimandabile && <Rimanda id={b.id} onRicarica={onRicarica} />}
        </span>
      )}
    </li>
  );
}

function Ordine({
  o,
  evento,
  posta,
  onRicarica,
}: {
  o: OrdineConBiglietti;
  evento: Evento;
  posta: Map<string, PostaAcquirente> | null;
  onRicarica: () => Promise<void>;
}) {
  return (
    <CartaOrdine ordine={o.ordine} onCambiata={onRicarica}>
      {posta && <RigaEmailOrdine evento={evento} ordine={o} posta={posta} />}
      <ul className="divide-y divide-gray-100 rounded-lg border border-gray-100 bg-gray-50/50 px-3">
        {o.biglietti.map((b) => (
          <RigaBiglietto key={b.id} b={b} evento={evento} onRicarica={onRicarica} />
        ))}
      </ul>
    </CartaOrdine>
  );
}

/**
 * La scheda Biglietti: i biglietti comprati sul sito, per ordine, con chi li
 * usa. Pixel legge gli ordini da solo ogni 10 minuti; da qui si puo' anche
 * leggere subito, o controllare senza salvare niente.
 */
export function SchedaBiglietti({ evento, biglietti, statoLettura, errore, onRicarica, onApri }: Props) {
  const { addNotification } = useNotifications();
  const [inCorso, setInCorso] = useState<'prova' | 'lettura' | null>(null);
  const [soloDaFare, setSoloDaFare] = useState(false);
  const { posta, errore: errorePosta } = usePostaAcquirenti(evento.id, biglietti);

  // Aperta la scheda, gli ordini arrivati sono visti: il pallino su Eventi si
  // azzera. Si rifa' quando arrivano biglietti nuovi mentre e' aperta.
  useEffect(() => {
    segnaBigliettiVisti();
  }, [biglietti.length]);

  const collegato = (evento.prodotti_sito ?? []).length > 0;
  const conto = contaBigliettiEvento(biglietti);
  const ordini = perOrdine(biglietti);
  const visibili = soloDaFare ? ordini.filter(ordineDaCompletare) : ordini;

  const leggi = async (prova: boolean) => {
    setInCorso(prova ? 'prova' : 'lettura');
    try {
      const esito = await eventiService.leggiOrdiniAdesso(prova);
      addNotification({
        type: esito.errori.length > 0 ? 'warning' : 'success',
        title: prova ? 'Controllo fatto, niente salvato' : 'Ordini letti dal sito',
        message: prova ? riepilogoProva(esito) : riepilogoLettura(esito),
      });
      if (!prova) await onRicarica();
    } catch (e) {
      addNotification({ type: 'error', title: 'Non riesco a leggere gli ordini dal sito', message: messaggioErrore(e) });
      await onRicarica();
    } finally {
      setInCorso(null);
    }
  };

  if (errore) {
    return (
      <Pannello>
        <div className="flex gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <p>Non riesco a leggere i biglietti: {errore}</p>
        </div>
      </Pannello>
    );
  }

  if (!collegato) {
    return (
      <Pannello>
        <div className="flex flex-col items-center gap-3 py-8 text-center">
          <ShoppingCart className="h-8 w-8 text-neutral-300" />
          <p className="text-sm font-medium text-gray-900">Nessun prodotto del sito collegato a questo evento</p>
          <p className="max-w-md text-xs text-neutral-500">
            Se i biglietti si comprano su slabsrl.com, scrivi nelle Impostazioni il numero del prodotto WooCommerce: Pixel
            leggerà gli ordini da solo.
          </p>
          <Button variant="outline" size="sm" onClick={() => onApri('impostazioni')}>
            <Settings className="mr-2 h-4 w-4" />
            Apri le Impostazioni
          </Button>
        </div>
      </Pannello>
    );
  }

  return (
    <div className="space-y-4">
      <Pannello
        titolo="Lettura degli ordini dal sito"
        sotto="Pixel legge da solo gli ordini di slabsrl.com ogni 10 minuti. Chi ha pagato riceve l'email per registrare i biglietti."
        azioni={
          <>
            <Button variant="outline" size="sm" disabled={inCorso !== null} onClick={() => void leggi(true)}>
              {inCorso === 'prova' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <SearchCheck className="mr-2 h-4 w-4" />}
              Controlla senza salvare
            </Button>
            <Button size="sm" disabled={inCorso !== null} onClick={() => void leggi(false)}>
              {inCorso === 'lettura' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
              Leggi adesso
            </Button>
          </>
        }
      >
        {letturaBloccata(statoLettura, new Date()) && (
          <div className="mb-3 flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <p>
              La lettura automatica parte ma non arriva in fondo (ultimo avvio {dataOraIt(statoLettura?.avviato_at)}). Di solito la
              chiave della lettura nella funzione non è uguale a quella nel database: va ricontrollata.
            </p>
          </div>
        )}
        {statoLettura?.errore ? (
          <div className="flex gap-3 rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <p>
              <span className="font-semibold">Ultima lettura non riuscita</span>
              {statoLettura.finito_at || statoLettura.avviato_at ? ` (${dataOraIt(statoLettura.finito_at ?? statoLettura.avviato_at)})` : ''}:{' '}
              {statoLettura.errore}
            </p>
          </div>
        ) : (
          <p className="text-xs text-neutral-600">
            {statoLettura?.finito_at
              ? `Ultima lettura: ${dataOraIt(statoLettura.finito_at)} · ${statoLettura.ordini_letti ?? 0} ordini letti`
              : 'Nessuna lettura ancora fatta.'}
          </p>
        )}
        <dl className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            { testo: 'Biglietti venduti', valore: conto.validi },
            { testo: 'Registrati', valore: conto.registrati },
            { testo: 'Da registrare', valore: conto.daRegistrare, evidenza: conto.daRegistrare > 0 },
            { testo: 'Annullati', valore: conto.annullati },
          ].map((c) => (
            <div key={c.testo} className="rounded-lg border border-gray-100 bg-gray-50/70 px-3 py-2">
              <dt className="text-[11px] text-neutral-500">{c.testo}</dt>
              <dd className={cn('text-xl font-semibold', c.evidenza ? 'text-amber-700' : 'text-gray-900')}>{c.valore}</dd>
            </div>
          ))}
        </dl>
      </Pannello>

      {ordini.length > 0 && <DaSentire evento={evento} ordini={ordini} posta={posta} errore={errorePosta} />}

      <Pannello
        titolo="Ordini"
        sotto="Per ogni ordine: chi usa ogni biglietto e la fattura. «Copia link» copia l'indirizzo per registrare quel biglietto, da mandare a chi non trova l'email. La fattura nasce su Fatture in Cloud già pagata; l'invio allo SDI si fa da lì."
        azioni={
          <label className="flex cursor-pointer items-center gap-2 text-xs text-neutral-600">
            <input type="checkbox" checked={soloDaFare} onChange={(e) => setSoloDaFare(e.target.checked)} className="accent-primary-600" />
            Solo con biglietti da registrare
          </label>
        }
        corpo="p-0"
      >
        {visibili.length === 0 ? (
          <p className="px-5 py-8 text-center text-sm text-neutral-500">
            {ordini.length === 0 ? 'Ancora nessun biglietto venduto.' : 'Tutti i biglietti sono registrati.'}
          </p>
        ) : (
          <ul className="space-y-3 p-3 md:p-4">
            {visibili.map((o) => (
              <Ordine key={o.ordine.id} o={o} evento={evento} posta={posta} onRicarica={onRicarica} />
            ))}
          </ul>
        )}
      </Pannello>
    </div>
  );
}
